import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getAnalyticsSettings, updateAnalyticsSettings } from "@/lib/admin-extra.functions";
import { CONVERSION_KEYS, EMPTY_LABELS, type ConversionKey } from "@/lib/analytics/types";
import { trackEvent } from "@/lib/analytics/gtag";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { GoogleAdsReport } from "@/components/admin/GoogleAdsReport";

export const Route = createFileRoute("/_authenticated/admin/analytics")({
  component: AdminAnalytics,
});

type Form = {
  enabled: boolean;
  ga4_measurement_id: string;
  ads_conversion_id: string;
  remarketing_enabled: boolean;
  enhanced_conversions: boolean;
  debug_mode: boolean;
  conversion_labels: Record<ConversionKey, string>;
};

const BLANK: Form = {
  enabled: true,
  ga4_measurement_id: "",
  ads_conversion_id: "",
  remarketing_enabled: true,
  enhanced_conversions: true,
  debug_mode: false,
  conversion_labels: { ...EMPTY_LABELS },
};

function AdminAnalytics() {
  const load = useServerFn(getAnalyticsSettings);
  const save = useServerFn(updateAnalyticsSettings);
  const qc = useQueryClient();
  const { data, isPending } = useQuery({ queryKey: ["analytics-settings"], queryFn: () => load() });
  const [form, setForm] = useState<Form>(BLANK);
  const [test, setTest] = useState<null | { ok: boolean; message: string }>(null);

  useEffect(() => {
    if (data) setForm({ ...BLANK, ...data, conversion_labels: { ...EMPTY_LABELS, ...data.conversion_labels } });
  }, [data]);

  const mutation = useMutation({
    mutationFn: () => save({ data: form }),
    onSuccess: () => {
      toast.success("Analytics settings saved. Reload the public site to apply them.");
      qc.invalidateQueries({ queryKey: ["analytics-settings"] });
      qc.invalidateQueries({ queryKey: ["analytics-config"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function runTest() {
    const g = typeof window !== "undefined" ? window.__nsAnalytics : undefined;
    if (!form.ga4_measurement_id && !form.ads_conversion_id) {
      setTest({ ok: false, message: "Add a GA4 Measurement ID or a Google Ads Conversion ID first." });
      return;
    }
    if (!g?.loaded) {
      setTest({
        ok: false,
        message:
          "The tag has not loaded in this browser tab. Save your settings, open the public site, accept cookies, then check GA4 DebugView or Google Tag Assistant.",
      });
      return;
    }
    trackEvent("admin_tracking_test", { source: "admin_dashboard" });
    setTest({
      ok: true,
      message: "Test event sent. It should appear in GA4 DebugView as admin_tracking_test within a minute.",
    });
  }

  if (isPending) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Analytics & Google Ads</h1>
        <p className="text-sm text-muted-foreground">
          Manage tracking IDs without touching code. Values entered here override the environment variables.
        </p>
      </div>

      <GoogleAdsReport />


      <Card>
        <CardHeader>
          <CardTitle>Tracking IDs</CardTitle>
          <CardDescription>Google Analytics 4 and Google Ads.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label className="text-sm font-medium">Tracking enabled</Label>
              <p className="text-xs text-muted-foreground">Turn all tracking on or off site wide.</p>
            </div>
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="ga4">GA4 Measurement ID</Label>
              <Input
                id="ga4"
                placeholder="G-XXXXXXXXXX"
                value={form.ga4_measurement_id}
                onChange={(e) => setForm({ ...form, ga4_measurement_id: e.target.value })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ads">Google Ads Conversion ID</Label>
              <Input
                id="ads"
                placeholder="AW-XXXXXXXXX"
                value={form.ads_conversion_id}
                onChange={(e) => setForm({ ...form, ads_conversion_id: e.target.value })}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <ToggleRow
              label="Remarketing"
              hint="Allow Google Ads remarketing tags."
              checked={form.remarketing_enabled}
              onChange={(v) => setForm({ ...form, remarketing_enabled: v })}
            />
            <ToggleRow
              label="Enhanced conversions"
              hint="Send hashed contact details with conversions."
              checked={form.enhanced_conversions}
              onChange={(v) => setForm({ ...form, enhanced_conversions: v })}
            />
            <ToggleRow
              label="Debug mode"
              hint="Show events in GA4 DebugView."
              checked={form.debug_mode}
              onChange={(v) => setForm({ ...form, debug_mode: v })}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Conversion labels</CardTitle>
          <CardDescription>
            Paste the label from each Google Ads conversion action, for example <code>AbC-D_efGh12</code>.
            Leave a label blank to send the GA4 event only.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {CONVERSION_KEYS.map((c) => (
            <div key={c.key} className="grid gap-1.5">
              <Label htmlFor={`label-${c.key}`}>{c.label}</Label>
              <Input
                id={`label-${c.key}`}
                value={form.conversion_labels[c.key]}
                onChange={(e) =>
                  setForm({
                    ...form,
                    conversion_labels: { ...form.conversion_labels, [c.key]: e.target.value },
                  })
                }
              />
              <p className="text-xs text-muted-foreground">{c.description}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save settings
        </Button>
        <Button variant="outline" onClick={runTest}>
          Test tracking
        </Button>
        {test && (
          <span className={`flex items-center gap-2 text-sm ${test.ok ? "text-emerald-600" : "text-destructive"}`}>
            {test.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            {test.message}
          </span>
        )}
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
      <div>
        <Label className="text-sm font-medium">{label}</Label>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
